
import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { ArrowLeftIcon, ClockIcon, UserIcon, MessageSquareIcon, CheckCircleIcon, AlertTriangleIcon, TrendingUpIcon, ImageIcon, XIcon, RefreshCwIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint, API_URL } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";

interface Ticket {
  id: string;
  title: string;
  description: string;
  type: string;
  category: string;
  subcategory?: string;
  priority: string;
  customer_priority?: string | null;
  contact_phone?: string | null;
  preferred_contact_time?: string | null;
  status: string;
  requester: { id: number; name: string; email: string } | null;
  assignee: { id: number; name: string; email: string } | null;
  company: { id: number; name: string; domain: string } | null;
  sla_remaining: string;
  sla_deadline: string;
  created_at: string;
  updated_at: string;
  nps_score?: number | null;
  nps_comment?: string | null;
  jira_key?: string | null;
}

interface Comment {
  id: number;
  content: string;
  is_internal: boolean;
  image_url?: string | null;
  created_at: string;
  source?: string;
  author_display_name?: string | null;
  author: { id: number; name: string; email: string; role: string; avatar_url?: string | null } | null;
}

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [npsComment, setNpsComment] = useState("");
  const [isSubmittingNPS, setIsSubmittingNPS] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [isReopening, setIsReopening] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const user = getUser();
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const userData = JSON.parse(user);
      setCurrentUser(userData);
    } catch (error) {
      console.error("Erro ao parsear usuário:", error);
    }

    fetchTicketData();
  }, [id, navigate]);

  const fetchTicketData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(`/api/tickets/${id}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTicket(data.ticket);
        setComments(data.comments || []);
        // Se já tem avaliação, preencher os campos
        if (data.ticket.nps_score !== null) {
          setNpsScore(data.ticket.nps_score);
          setNpsComment(data.ticket.nps_comment || "");
        }
      } else {
        toast({
          title: t.ticketDetails.error,
          description: t.ticketDetails.notFoundDesc,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t.ticketDetails.error,
        description: t.ticketDetails.loadError,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: t.ticketDetails.fileTooBig,
        description: t.ticketDetails.fileTooBigDesc,
        variant: "destructive",
      });
      return;
    }

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast({
        title: t.ticketDetails.invalidFileType,
        description: t.ticketDetails.invalidFileTypeDesc,
        variant: "destructive",
      });
      return;
    }

    setSelectedImage(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    // Resetar input de arquivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddComment = async () => {
    if ((!newComment.trim() && !selectedImage) || !ticket) {
      toast({
        title: t.ticketDetails.attention,
        description: t.ticketDetails.addCommentOrImage,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const token = getToken();
      
      if (!token) {
        throw new Error("Token de autenticação não encontrado. Por favor, faça login novamente.");
      }
      
      const formData = new FormData();
      formData.append('content', newComment || '');
      formData.append('is_internal', 'false');
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      const response = await fetch(getApiEndpoint(`/api/tickets/${ticket.id}/comments`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setComments([...comments, data.comment]);
        setNewComment("");
        setSelectedImage(null);
        setImagePreview(null);
        // Resetar input de arquivo
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        toast({
          title: t.ticketDetails.commentAdded,
          description: t.ticketDetails.commentAddedDesc,
        });
        fetchTicketData();
      } else {
        let errorMessage = "Erro ao adicionar comentário";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // Se não conseguir fazer parse do JSON, usar mensagem genérica baseada no status
          if (response.status === 401) {
            errorMessage = "Sessão expirada. Por favor, faça login novamente.";
          } else if (response.status === 403) {
            errorMessage = "Você não tem permissão para realizar esta ação.";
          } else if (response.status === 400) {
            errorMessage = "Dados inválidos. Verifique os campos e tente novamente.";
          }
        }
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("Erro ao adicionar comentário:", error);
      toast({
        title: t.ticketDetails.error,
        description: error.message || t.ticketDetails.loadError,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitNPS = async () => {
    if (!ticket || npsScore === null) return;

    setIsSubmittingNPS(true);
    try {
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(`/api/tickets/${ticket.id}/rate`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nps_score: npsScore,
          comment: npsComment.trim() || null
        }),
      });

      if (response.ok) {
        toast({
          title: t.ticketDetails.ratingSent,
          description: t.ticketDetails.ratingSentDesc,
        });
        // Limpar campos
        setNpsScore(null);
        setNpsComment("");
        // Atualizar dados do ticket
        fetchTicketData();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erro ao enviar avaliação");
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível enviar a avaliação.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingNPS(false);
    }
  };

  const handleReopenTicket = async () => {
    if (!ticket) return;

    // Validar motivo obrigatório
    if (!reopenReason.trim()) {
      toast({
        title: t.ticketDetails.reopenReasonRequired,
        description: t.ticketDetails.reopenReasonDesc,
        variant: "destructive",
      });
      return;
    }

    setIsReopening(true);
    try {
      const token = getToken();
      
      const response = await fetch(getApiEndpoint(`/api/tickets/${ticket.id}/reopen`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason: reopenReason.trim() }),
      });

      if (response.ok) {
        toast({
          title: t.ticketDetails.ticketReopened,
          description: t.ticketDetails.ticketReopenedDesc,
        });
        setReopenReason("");
        fetchTicketData();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erro ao reabrir chamado");
      }
    } catch (error: any) {
      toast({
        title: t.ticketDetails.error,
        description: error.message || t.ticketDetails.loadError,
        variant: "destructive",
      });
    } finally {
      setIsReopening(false);
    }
  };

  const convertStatus = (status: string) => {
    switch (status) {
      case "open": return "Aberto";
      case "in_progress": return "Em atendimento";
      case "waiting_response": return "Aguardando resposta";
      case "resolved": return "Resolvido";
      case "closed": return "Fechado";
      default: return status;
    }
  };

  const convertPriority = (priority: string) => {
    switch (priority) {
      case "critical": return "Crítica";
      case "high": return "Alta";
      case "medium": return "Média";
      case "low": return "Baixa";
      default: return priority;
    }
  };

  const convertType = (type: string) => {
    switch (type) {
      case "incident": return "Incidente";
      case "request": return "Requisição";
      case "problem": return "Problema";
      case "change": return "Mudança";
      default: return type;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Aberto": return "bg-blue-100 text-blue-800";
      case "Em atendimento": return "bg-yellow-100 text-yellow-800";
      case "Aguardando resposta": return "bg-orange-100 text-orange-800";
      case "Resolvido": return "bg-green-100 text-green-800";
      case "Fechado": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Crítica": return "bg-red-100 text-red-800";
      case "Alta": return "bg-orange-100 text-orange-800";
      case "Média": return "bg-yellow-100 text-yellow-800";
      case "Baixa": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "Incidente": return <AlertTriangleIcon className="h-5 w-5 text-red-600" />;
      case "Requisição": return <CheckCircleIcon className="h-5 w-5 text-blue-600" />;
      case "Problema": return <TrendingUpIcon className="h-5 w-5 text-purple-600" />;
      case "Mudança": return <ClockIcon className="h-5 w-5 text-green-600" />;
      default: return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.ticketDetails.loading}</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t.ticketDetails.notFound}</h2>
          <Link to="/chamados">
            <Button>{t.ticketDetails.backToTickets}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const ticketStatus = convertStatus(ticket.status);
  const ticketPriority = convertPriority(ticket.priority);
  const ticketType = convertType(ticket.type);

  const formatDateTime = (value?: string | null) => {
    if (!value) return "";
    return new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8">
          <Link to="/chamados" className="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-4">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            {t.ticketDetails.backToTickets}
          </Link>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {getTypeIcon(ticketType)}
                <h1 className="text-3xl font-bold text-gray-900">#{ticket.id}</h1>
                <Badge className={getStatusColor(ticketStatus)}>{ticketStatus}</Badge>
                <Badge className={getPriorityColor(ticketPriority)}>{ticketPriority}</Badge>
              </div>
              <h2 className="text-xl text-gray-700 mb-2">{ticket.title}</h2>
              <p className="text-gray-600">
                {t.ticketDetails.createdAt} {formatDateTime(ticket.created_at)} • 
                {t.ticketDetails.updatedAt} {formatDateTime(ticket.updated_at)}
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Conteúdo Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Descrição */}
            <Card>
              <CardHeader>
                <CardTitle>{t.ticketDetails.description}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
              </CardContent>
            </Card>

            {/* Timeline de Comentários */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center">
                      <MessageSquareIcon className="h-5 w-5 mr-2" />
                      {t.ticketDetails.communication} ({comments.length})
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {t.ticketDetails.communicationDesc}
                      {ticket?.jira_key && (
                        <span className="block mt-1 text-blue-600 dark:text-blue-400 text-xs">
                          Sincronizado com Jira. Respostas da equipe no Jira aparecem aqui. Use &quot;Atualizar&quot; para ver o que há de novo.
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  {ticket?.jira_key && (
                    <Button type="button" variant="outline" size="sm" onClick={() => fetchTicketData()} className="shrink-0">
                      <RefreshCwIcon className="h-4 w-4 mr-1" />
                      Atualizar
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquareIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>{t.ticketDetails.noMessages}</p>
                    <p className="text-sm">{t.ticketDetails.startConversation}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {comments.map((comment, index) => (
                      <div key={comment.id} className="relative">
                        {index > 0 && (
                          <div className="absolute left-5 top-0 w-px h-6 bg-gray-200 -translate-y-6"></div>
                        )}
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <Avatar className="h-10 w-10 border-2 border-gray-200 dark:border-gray-600">
                              <AvatarImage
                                src={comment.author?.avatar_url ? (comment.author.avatar_url.startsWith("http") ? comment.author.avatar_url : getApiEndpoint(comment.author.avatar_url)) : undefined}
                                alt={comment.author?.name}
                              />
                              <AvatarFallback className="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-sm font-medium">
                                {(comment.author?.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {comment.author?.name || comment.author_display_name || t.ticketDetails.user}
                              </p>
                              {comment.source === 'jira' && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                  Jira
                                </span>
                              )}
                              <p className="text-xs text-gray-500">
                                {new Date(comment.created_at).toLocaleString('pt-BR')}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              {comment.content && (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">{comment.content}</p>
                              )}
                              {comment.image_url && (
                                <div className="mt-2">
                                  <img 
                                    src={comment.image_url?.startsWith('http') ? comment.image_url : `${API_URL || ''}${comment.image_url}`}
                                    alt="Imagem anexada"
                                    className="max-w-full h-auto rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => window.open(comment.image_url?.startsWith('http') ? comment.image_url : `${API_URL || ''}${comment.image_url}`, '_blank')}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Adicionar Comentário */}
                {ticket.status !== "closed" && ticket.status !== "resolved" && (
                  <div className="border-t pt-4">
                    <div className="space-y-3">
                      <Textarea
                        placeholder={t.ticketDetails.addCommentPlaceholder}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value.slice(0, 1000))}
                        rows={3}
                        maxLength={1000}
                      />
                      <p className="text-xs text-gray-500 text-right">
                        {newComment.length}/1000
                      </p>
                      
                      {/* Upload de Imagem */}
                      <div className="space-y-2">
                        <Label htmlFor="image-upload" className="text-sm font-medium text-gray-700">
                          {t.ticketDetails.attachImage}
                        </Label>
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="image-upload"
                            className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <ImageIcon className="h-4 w-4 mr-2 text-gray-600" />
                            <span className="text-sm text-gray-600">{t.ticketDetails.selectImage}</span>
                            <input
                              ref={fileInputRef}
                              id="image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleImageSelect}
                              className="hidden"
                            />
                          </label>
                          {selectedImage && (
                            <span className="text-sm text-gray-600">
                              {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          )}
                        </div>
                        
                        {/* Preview da Imagem */}
                        {imagePreview && (
                          <div className="relative inline-block mt-2">
                            <div className="relative">
                              <img
                                src={imagePreview}
                                alt="Preview da imagem anexada"
                                className="max-w-full max-h-64 h-auto rounded-lg border-2 border-gray-300 shadow-sm object-contain"
                              />
                              <button
                                type="button"
                                onClick={removeImage}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-lg"
                                aria-label="Remover imagem"
                              >
                                <XIcon className="h-4 w-4" />
                              </button>
                            </div>
                            {selectedImage && (
                              <p className="text-xs text-gray-500 mt-1">
                                {selectedImage.name} ({(selectedImage.size / 1024 / 1024).toFixed(2)} MB)
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <Button 
                        onClick={handleAddComment} 
                        disabled={isLoading || (!newComment.trim() && !selectedImage)}
                        className="w-full"
                      >
                        {isLoading ? t.ticketDetails.sendingComment : t.ticketDetails.sendComment}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar de Informações */}
          <div className="space-y-6">
            {/* SLA */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  {t.ticketDetails.sla}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t.ticketDetails.timeRemaining}</p>
                    <p className="text-lg font-semibold text-blue-600">{ticket.sla_remaining}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t.ticketDetails.deadline}</p>
                    <p className="text-sm text-gray-700">
                      {new Date(ticket.sla_deadline).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detalhes do Chamado */}
            <Card>
              <CardHeader>
                <CardTitle>{t.ticketDetails.details}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t.ticketDetails.type}</p>
                  <p className="text-sm text-gray-900">{ticketType}</p>
                </div>
                
                <Separator />

                <div>
                  <p className="text-sm font-medium text-gray-600">{t.ticketDetails.priority}</p>
                  <Badge className={getPriorityColor(convertPriority(ticket.priority))}>
                    {convertPriority(ticket.priority)}
                  </Badge>
                  {ticket.customer_priority && ticket.customer_priority !== ticket.priority && (
                    <p className="text-xs text-gray-500 mt-2">
                      {t.ticketDetails.priorityReportedAtOpen}{" "}
                      <span className="font-medium">{convertPriority(ticket.customer_priority)}</span>
                    </p>
                  )}
                </div>

                <Separator />
                
                <div>
                  <p className="text-sm font-medium text-gray-600">{t.ticketDetails.category}</p>
                  <p className="text-sm text-gray-900">{ticket.category}</p>
                  {ticket.subcategory && (
                    <p className="text-xs text-gray-600">{ticket.subcategory}</p>
                  )}
                </div>
                
                <Separator />

                {(ticket.contact_phone || ticket.preferred_contact_time) && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{t.ticketDetails.contactTime}</p>
                      {ticket.contact_phone && (
                        <p className="text-sm text-gray-900">📞 {ticket.contact_phone}</p>
                      )}
                      {ticket.preferred_contact_time && (
                        <p className="text-sm text-gray-700 mt-1">⏰ {ticket.preferred_contact_time}</p>
                      )}
                    </div>

                    <Separator />
                  </>
                )}
                
                <div>
                  <p className="text-sm font-medium text-gray-600">{t.ticketDetails.assignee}</p>
                  <p className="text-sm text-gray-900">
                    {ticket.assignee?.name || t.ticketDetails.notAssigned}
                  </p>
                </div>
                
                <Separator />
                
                <div>
                  <p className="text-sm font-medium text-gray-600">{t.ticketDetails.requester}</p>
                  <p className="text-sm text-gray-900">
                    {ticket.requester?.name || t.ticketDetails.notInformed}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Avaliação NPS (se resolvido ou fechado) */}
            {(ticket.status === "resolved" || ticket.status === "closed") && (
              <Card className={ticket.nps_score !== null ? "border-green-200 bg-green-50" : ""}>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2 text-green-600" />
                    {ticket.nps_score !== null ? t.ticketDetails.ratingSentTitle : t.ticketDetails.rateService}
                  </CardTitle>
                  <CardDescription>
                    {ticket.nps_score !== null 
                      ? t.ticketDetails.thanksForRating 
                      : t.ticketDetails.yourOpinionHelps}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {ticket.nps_score !== null ? (
                    // Mostrar avaliação já enviada
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-2">{t.ticketDetails.yourNpsRating}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-100">
                            <span className="text-2xl font-bold text-blue-600">{ticket.nps_score}</span>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700">
                              {ticket.nps_score >= 9 ? t.ticketDetails.promoter : ticket.nps_score >= 7 ? t.ticketDetails.neutral : t.ticketDetails.detractor}
                            </p>
                            <p className="text-xs text-gray-500">{t.ticketDetails.scaleZeroToTen}</p>
                          </div>
                        </div>
                      </div>
                      {ticket.nps_comment && (
                        <div>
                          <p className="text-sm font-medium text-gray-600 mb-1">Seu Comentário</p>
                          <p className="text-sm text-gray-700 bg-white p-3 rounded-lg border">
                            {ticket.nps_comment}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Formulário de avaliação
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-2 block">
                          {t.ticketDetails.howLikelyRecommend}
                        </Label>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex gap-1 flex-wrap">
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                              <button
                                key={score}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setNpsScore(score);
                                }}
                                className={`w-10 h-10 rounded-lg border-2 transition-all flex-shrink-0 ${
                                  npsScore === score
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                                }`}
                              >
                                {score}
                              </button>
                            ))}
                          </div>
                        </div>
                        {npsScore !== null && (
                          <div className="mt-3 space-y-4">
                            <p className="text-xs text-gray-500">
                              {npsScore >= 9 
                                ? `${t.ticketDetails.promoter} - ${t.ticketDetails.promoterDesc}` 
                                : npsScore >= 7 
                                ? `${t.ticketDetails.neutral} - ${t.ticketDetails.neutralDesc}`
                                : `${t.ticketDetails.detractor} - ${t.ticketDetails.detractorDesc}`}
                            </p>
                            
                            <div>
                              <Label htmlFor="nps-comment" className="text-sm font-medium text-gray-700 mb-2 block">
                                {t.ticketDetails.wouldYouComment}
                              </Label>
                              <p className="text-xs text-gray-500 mb-2">
                                {t.ticketDetails.commentHint}
                              </p>
                              <Textarea
                                id="nps-comment"
                                placeholder={t.ticketDetails.commentPlaceholder}
                                value={npsComment}
                                onChange={(e) => setNpsComment(e.target.value)}
                                rows={4}
                                className="resize-none"
                              />
                            </div>
                            
                            <Button 
                              type="button"
                              className="w-full"
                              onClick={(e) => {
                                e.preventDefault();
                                handleSubmitNPS();
                              }}
                              disabled={isSubmittingNPS}
                            >
                              {isSubmittingNPS ? t.ticketDetails.sendingComment : t.ticketDetails.submitRating}
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-500 text-center">
                        {t.ticketDetails.ratingHelps}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Reabrir Chamado (se resolvido ou fechado e o usuário é o dono) */}
            {(ticket.status === "resolved" || ticket.status === "closed") && 
             currentUser && 
             ticket.requester && 
             currentUser.id === ticket.requester.id && (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertTriangleIcon className="h-5 w-5 mr-2 text-orange-600" />
                    {t.ticketDetails.reopenSectionTitle}
                  </CardTitle>
                  <CardDescription>
                    {t.ticketDetails.reopenSectionDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="reopen-reason" className="text-sm font-medium text-gray-700 mb-2 block">
                      {t.ticketDetails.reopenReasonLabel} <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="reopen-reason"
                      placeholder={t.ticketDetails.reopenReasonPlaceholder}
                      value={reopenReason}
                      onChange={(e) => setReopenReason(e.target.value)}
                      rows={4}
                      required
                      className="resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t.ticketDetails.reopenReasonHint}
                    </p>
                  </div>
                  
                  <Button 
                    type="button"
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    onClick={handleReopenTicket}
                    disabled={isReopening || !reopenReason.trim()}
                  >
                    {isReopening ? t.ticketDetails.reopening : t.ticketDetails.reopenTicket}
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    {t.ticketDetails.reopenDeadlineHint}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TicketDetails;
